import { DropDown, useErrorState } from '@finbeat/react-toolkit';
import { getSelectList } from 'axios/dictionaryApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { ClientDocsInfo } from 'models/api/client';
import { RequestDocsInfo } from 'models/api/order';
import { Dispatch, Key, RefObject, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Container, FormGroup } from 'reactstrap';
import i18n from 'utils/i18n';
import ClientFile from '../Additions/ClientFile';
import '../OrderInfo.scss';

const ClientFilesTab = ({
    documentsQueryFn,
    isOpen,
    canRequestingDocs,
    isEditable,
    modalRef,
    clientId,
    orderId,
    setRequestedDocs,
}: {
    documentsQueryFn: () => Promise<ClientDocsInfo>;
    isOpen: boolean;
    isEditable: boolean;
    canRequestingDocs: boolean;
    modalRef?: RefObject<HTMLElement>;
    clientId: Key;
    orderId?: Key;
    setRequestedDocs?: Dispatch<SetStateAction<RequestDocsInfo[]>>;
}) => {
    const api = useApi();
    const { hasRole } = useAuth();

    const className = 'order-info-documents';
    const files = ['Selfie'];
    const [clientFileTypes, setClientFileTypes] = useState<DropDown[]>();
    const [editReasonDict, setEditReasonDict] = useState<DropDown[]>();
    const [clientDocsInfo, setClientDocsInfo] = useState<ClientDocsInfo | null>();
    const [wasOpened, setWasOpened] = useState(false);
    const { handleRequestError } = useErrorState();

    useEffect(() => {
        !wasOpened && setWasOpened(isOpen);
    }, [isOpen]);

    const documentsQuery = useQuery('clientFiles', () => documentsQueryFn(), {
        onSuccess: (data) => {
            setClientDocsInfo(data);
        },
        enabled: wasOpened && !clientDocsInfo,
        onError: (e) => handleRequestError(e),
    });

    const clientFileTypesQuery = useQuery(
        'clientFileTypes',
        () => getSelectList(api, ['ClientFileTypes', 'EditingReason'], i18n.language),
        {
            onSuccess: (data) => {
                setClientFileTypes(data['ClientFileTypes']);
                setEditReasonDict(data['EditingReason']);
            },
            enabled: wasOpened && !clientFileTypes,
            onError: (e) => handleRequestError(e),
        }
    );

    const isCanRequestDocsWithRules = useMemo(
        () => hasRole('clientsRequestDocuments') && canRequestingDocs,
        [hasRole, isEditable]
    );

    return (
        <Container className={className}>
            {files.map((value) => {
                const docs = clientDocsInfo?.docs != null ? clientDocsInfo?.docs[value] : null;
                return (
                    <FormGroup key={value}>
                        <ClientFile
                            fileType={value}
                            header={clientFileTypes?.find((x) => x.id === value)?.name ?? ''}
                            value={docs && docs[0]}
                            loading={documentsQuery.isFetching || clientFileTypesQuery.isFetching}
                            canRequestingDocs={isCanRequestDocsWithRules}
                            editable={false}
                            editReasons={editReasonDict}
                            modalRef={modalRef}
                            phoneNumber={clientDocsInfo?.phoneNumber ?? null}
                            docNum={clientDocsInfo?.docNum ?? null}
                            clientId={clientId}
                            orderId={orderId}
                            setRequestedDocs={setRequestedDocs}
                        />
                    </FormGroup>
                );
            })}
        </Container>
    );
};

export default ClientFilesTab;
